import type { Node } from "@xyflow/react";

// ── SECTION LIBRARY bands ────────────────────────────────────────────────────
// Split (Erez 2026-07-17): the Relevance REFERENCES live on the Marketing RESEARCH
// board; OUR curated sections (by category) live on the Marketing board, moved up.
// Both take a parameterized origin (x0,y0) so each board places them cleanly.
// Images served from a tailormind-site immutable deploy hash.

const IMGf = (hash: string, bust: string) => (p: string) => `${hash}/board/${p}${bust}`;

// The 6 Relevance AI reference full-page captures — a horizontal row. (Research board.)
export function band7References(hash: string, bust: string, x0 = 0, y0 = 0): Node[] {
  const out: Node[] = [];
  const IMG = IMGf(hash, bust);
  const REFS = ["workforce", "product", "enterprise", "agents", "learn", "bootcamp"];
  const REF_PITCH = 820, REF_W = 760;
  out.push({ id: "seclib-refzone", type: "sectionTitle", position: { x: x0, y: y0 - 380 }, data: { title: "Relevance AI — reference pages", sub: "The richer content-page style we studied (Erez likes these). Full-page captures of Relevance AI's product + resources pages." }, draggable: false, selectable: false });
  REFS.forEach((r, i) => {
    const x = x0 + i * REF_PITCH;
    out.push({ id: `seclib-ref-${r}-label`, type: "title", position: { x, y: y0 - 80 }, data: { title: r, tag: `relevance/${r}`, ref: false }, draggable: false, selectable: false });
    out.push({ id: `seclib-ref-${r}`, type: "siteFrame", position: { x, y: y0 }, data: { label: r, tag: "REF", img: IMG(`reference/relevance-${r}.jpg`), w: REF_W }, draggable: false, selectable: false });
  });
  return out;
}

// OUR 38 sections grouped by CATEGORY (7 categories). (Marketing board, moved up.)
export function band7Sections(hash: string, bust: string, x0 = 0, y0 = 0): Node[] {
  const out: Node[] = [];
  const IMG = IMGf(hash, bust);
  out.push({ id: "seclib-title", type: "sectionTitle", position: { x: x0, y: y0 - 380 }, data: { title: "Section Library — our sections, by category", sub: "Our reusable content-page section patterns in our design, grouped by category. Pick the ones for each real page." }, draggable: false, selectable: false });

  const CATEGORIES: { name: string; ids: string[] }[] = [
    { name: "Heroes", ids: ["centered-hero", "hero-with-preview"] },
    { name: "Service hero · Discovery & AI Audit (pick 1 of 4)", ids: ["hero-discovery-1", "hero-discovery-2", "hero-discovery-3", "hero-discovery-4"] },
    { name: "Service hero · Build & Implement (pick 1 of 4)", ids: ["hero-build-1", "hero-build-2", "hero-build-3", "hero-build-4"] },
    { name: "Service hero · Run & Grow (pick 1 of 4)", ids: ["hero-run-1", "hero-run-2", "hero-run-3", "hero-run-4"] },
    { name: "Service hero · Enablement (pick 1 of 4)", ids: ["hero-enablement-1", "hero-enablement-2", "hero-enablement-3", "hero-enablement-4"] },
    { name: "Feature sections", ids: ["icon-row", "bento-feature", "alt-feature", "three-up-cards", "checklist-split", "big-mock-showcase", "feature-accordion", "tabs-showcase", "feature-split-right", "feature-split-left", "feature-2x2", "feature-rows", "bento-asymmetric", "pillars-row"] },
    { name: "Process / how it works", ids: ["numbered-steps", "process-timeline", "steps-cards"] },
    { name: "Proof & comparison", ids: ["testimonial-video", "comparison-table", "before-after", "logo-marquee", "testimonial-trio", "comparison-two-col"] },
    { name: "Stats & metrics", ids: ["stat-roi", "metric-callout", "mini-stats-row", "stat-cards"] },
    { name: "Editorial & content", ids: ["two-col-prose", "quote-band", "callout-band", "resource-cards"] },
    { name: "Pricing · CTA · FAQ", ids: ["pricing-tiers", "cta-band", "cta-split", "faq-accordion", "integrations-grid"] },
  ];

  const COL_PITCH = 1560, ROW_PITCH = 1180, SEC_W = 1420, COLS = 4, CAT_GAP = 520;
  // Reflow (Erez 2026-07-19): DON'T stack all 11 categories in one ~24k-tall column
  // (it ran down into the Services zone). Flow them into 3 balanced MEGA-COLUMNS side
  // by side — wide + short. In category order, break to the next mega-col once the
  // running height passes total/3. Col 1 ends up holding all the hero picks together.
  const MEGA_COLS = 3, MEGA_PITCH = 7000;
  const catH = (n: number) => Math.ceil(n / COLS) * ROW_PITCH + CAT_GAP;
  const target = CATEGORIES.reduce((s, c) => s + catH(c.ids.length), 0) / MEGA_COLS;
  let mc = 0, colH = 0;
  CATEGORIES.forEach((cat, ci) => {
    if (mc < MEGA_COLS - 1 && colH >= target) { mc++; colH = 0; }
    const cx = x0 + mc * MEGA_PITCH;
    const cy = y0 + colH;
    out.push({ id: `seclib-cat-${ci}`, type: "title", position: { x: cx, y: cy - 210 }, data: { title: cat.name, tag: `${cat.ids.length} sections`, ref: false }, draggable: false, selectable: false });
    cat.ids.forEach((s, i) => {
      const col = i % COLS, row = Math.floor(i / COLS);
      const x = cx + col * COL_PITCH, sy = cy + row * ROW_PITCH;
      out.push({ id: `seclib-sec-${s}-label`, type: "title", position: { x, y: sy - 70 }, data: { title: s, tag: "SECTION", ref: false }, draggable: false, selectable: false });
      out.push({ id: `seclib-sec-${s}`, type: "siteFrame", position: { x, y: sy }, data: { label: s, tag: "OURS", img: IMG(`sections/${s}.jpg`), w: SEC_W }, draggable: false, selectable: false });
    });
    colH += catH(cat.ids.length);
  });
  return out;
}
